import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { BrandName, ColorSchema, StyleSchema } from '@models/color-schema.model';
import { BRAND_CONFIGS } from '@config/brandConfigs';

// Lesen des Brands aus der Umgebungsvariable
const getBrandFromEnv = (): BrandName => {
  const envBrand = process.env.NEXT_PUBLIC_DEFAULT_BRAND as BrandName;
  return BRAND_CONFIGS[envBrand] ? envBrand : 'klier';
};

const initialState: StyleSchema = BRAND_CONFIGS[getBrandFromEnv()];

export const styleSchemaSlice = createSlice({
  name: 'styleSchema',
  initialState,
  reducers: {
    setColors: (state, action: PayloadAction<ColorSchema>) => {
      state.colors = action.payload;
    },
    setPrimaryColor: (state, action: PayloadAction<ColorSchema['primary']>) => {
      state.colors.primary = action.payload;
    },
    setSecondaryColor: (state, action: PayloadAction<ColorSchema['secondary']>) => {
      state.colors.secondary = action.payload;
    },
    setAccentColor: (state, action: PayloadAction<ColorSchema['accent']>) => {
      state.colors.accent = action.payload;
    },
    setBrandSchema: (state, action: PayloadAction<BrandName>) => {
      return BRAND_CONFIGS[action.payload] || BRAND_CONFIGS.klier;
    },
    setTypography: (state, action: PayloadAction<StyleSchema['typography']>) => {
      if (state.typography) {
        state.typography = { ...state.typography, ...action.payload };
      } else {
        state.typography = action.payload;
      }
    },
    setDisturber: (state, action: PayloadAction<StyleSchema['disturber']>) => {
      state.disturber = action.payload;
    },
    setHero: (state, action: PayloadAction<StyleSchema['hero']>) => {
      state.hero = action.payload;
    },
    setBackgrounds: (state, action: PayloadAction<StyleSchema['backgrounds']>) => {
      state.backgrounds = action.payload;
    }
  }
});

export const { 
  setColors, 
  setPrimaryColor, 
  setSecondaryColor, 
  setAccentColor,
  setBrandSchema,
  setTypography,
  setDisturber,
  setHero,
  setBackgrounds
} = styleSchemaSlice.actions;

export const selectStyleSchema = (state: RootState) => state.styleSchema;
export const selectColors = (state: RootState) => state.styleSchema.colors;
export const selectBrandName = (state: RootState) => state.styleSchema.name;
export const selectBrandString = (state: RootState) => state.styleSchema.brandString;
export const selectDisturber = (state: RootState) => state.styleSchema.disturber;
export const selectLogo = (state: RootState) => state.styleSchema.logo;
export const selectTypography = (state: RootState) => state.styleSchema.typography;
export const selectHero = (state: RootState) => state.styleSchema.hero;
export const selectBackgrounds = (state: RootState) => state.styleSchema.backgrounds;
export const selectNewsletterPath = (state: RootState) => state.styleSchema?.newsletter?.path;
export const selectNewsletterLink = (state: RootState) => state.styleSchema?.newsletter?.link;
export const selectFaqLink = (state: RootState) => state.styleSchema?.faq?.link;
export const selectContactFormLink = (state: RootState) => state.styleSchema?.contactForm?.link;
export const selectContactFormBackground = (state: RootState) => state.styleSchema?.contactForm?.background;
export const selectBrandId = (state: RootState) => state?.styleSchema?.brandId;

// Selektoren für Terms-Links
export const selectTermsLink = (state: RootState) => state?.styleSchema?.terms?.termsLink;
export const selectPrivacyLink = (state: RootState) => state?.styleSchema?.terms?.privacyLink;
export const selectLoyaltyLink = (state: RootState) => state?.styleSchema?.terms?.loyaltyLink;

export default styleSchemaSlice.reducer;
